import { useState, useEffect, ReactNode, FC, useCallback } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';

import { Global } from '../constants';
import { getItem } from '../utils/localStorageControl';
import { userGetData } from '../redux/users/actionCreator';
import Loader from '../components/atoms/loader.component';

interface ProtectedRouteProps {
  children: ReactNode;
  route: string;
  userGetData: Function,
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, route, userGetData }) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [isRedirectedPage, setIsRedirectedPage] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const validateProtectedRoute = useCallback(async () => {
    try {
      setError(undefined);
      const containsUnprotectedRoutes = Global.UNPROTECTED_ROUTES.filter((path) => route.startsWith(path));
      const containsAccessRoutes = Global.ACCESS_ROUTES.filter((path) => route.startsWith(path));
      const isPathProtected = route !== '/' && containsUnprotectedRoutes.length === 0;
      const isAccessRoutes = route === '/' || containsAccessRoutes.length > 0;
      const hasToken = Boolean(getItem('access_token'));
      const hasLoginCookie = Boolean(Cookies.get('logedIn'));
      const isAuthenticated = hasToken && hasLoginCookie;

      if (!isAuthenticated && isPathProtected) {
        setIsRedirectedPage(true);
        navigate("/");
        return;
      }

      if (isAuthenticated) {
        if (isAccessRoutes) {
          setIsRedirectedPage(true);
          navigate("/admin/shoppings");
          return;
        } else {
          // Agregar timeout para evitar cargas infinitas
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Timeout')), 15000); // 15 segundos
          });

          await Promise.race([
            userGetData(),
            timeoutPromise
          ]);
        }
      }

      setIsProcessing(false);
      setIsRedirectedPage(false);
    } catch (error) {
      console.error('Error in validateProtectedRoute:', error);
      setError('Error al cargar la aplicación. Por favor, recarga la página.');
      setIsProcessing(false);
      setIsRedirectedPage(false);
    }
  }, [route, navigate, userGetData]);

  useEffect(() => {
    validateProtectedRoute();
  }, [validateProtectedRoute]);

  const handleRetry = () => {
    setIsProcessing(true);
    setError(undefined);
    validateProtectedRoute();
  };

  return (
    <>
      {(isProcessing || isRedirectedPage) && (
        <Loader
          isFullPage
          error={error}
          onRetry={handleRetry}
        />
      )}
      {!isProcessing && !isRedirectedPage && children}
    </>
  );
};

export default connect(null, { userGetData })(ProtectedRoute);
